import { useEffect, useMemo, useState } from "react";
import { api, absUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Sparkles,
  RefreshCcw,
  CheckCircle2,
  Loader2,
  ArrowRight,
  Upload,
  Star,
  XCircle,
  Trash2,
} from "lucide-react";

const VIEW_LABELS = {
  portrait: "Portrait",
  profile: "Side profile",
  full_body: "Full body",
  expressions: "Expressions",
};

function EmptyState({ onStart }) {
  return (
    <div
      data-testid="characters-empty"
      className="mx-auto max-w-xl rounded-2xl border border-dashed border-orange-300 bg-white/70 p-10 text-center shadow-sm"
    >
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-orange-200 to-rose-200 text-orange-700">
        <Sparkles className="h-8 w-8" />
      </div>
      <h2 className="text-2xl font-bold text-stone-800">Create your first character</h2>
      <p className="mt-2 text-stone-600">
        Turn reference photos into a child-safe stylized cartoon that will star
        in every Dom&apos;s Journey episode.
      </p>
      <Button
        data-testid="start-designer-btn"
        onClick={onStart}
        className="mt-6 h-11 rounded-full bg-gradient-to-r from-orange-500 to-rose-500 px-6 text-white"
      >
        <Sparkles className="mr-2 h-4 w-4" />
        Start character designer
      </Button>
    </div>
  );
}

function ReferencePhotoPicker({ samples, selected, setSelected, onUploadUrl }) {
  const [url, setUrl] = useState("");
  const toggle = (ref) => {
    setSelected((prev) =>
      prev.includes(ref.url) ? prev.filter((u) => u !== ref.url) : [...prev, ref.url],
    );
  };
  const addCustom = () => {
    const v = url.trim();
    if (!v) return;
    onUploadUrl(v);
    setUrl("");
  };
  return (
    <div data-testid="ref-picker" className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
        {samples.map((s) => {
          const on = selected.includes(s.url);
          return (
            <button
              type="button"
              key={s.id}
              data-testid={`ref-${s.id}`}
              onClick={() => toggle(s)}
              className={`group relative aspect-square overflow-hidden rounded-xl border-2 transition ${
                on
                  ? "border-orange-500 ring-2 ring-orange-300"
                  : "border-orange-100 hover:border-orange-300"
              }`}
            >
              <img src={s.url} alt={s.label} className="h-full w-full object-cover" />
              {on ? (
                <span className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-orange-500 text-white shadow">
                  <CheckCircle2 className="h-4 w-4" />
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <Input
          data-testid="custom-ref-url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Paste an image URL (optional)…"
          className="border-orange-200"
        />
        <Button
          type="button"
          onClick={addCustom}
          variant="outline"
          className="border-orange-300"
          data-testid="custom-ref-add"
        >
          <Upload className="mr-1 h-4 w-4" />
          Add
        </Button>
      </div>
    </div>
  );
}

function StylePicker({ styles, value, onChange }) {
  return (
    <div data-testid="style-picker" className="grid gap-3 sm:grid-cols-2">
      {styles.map((s) => {
        const on = value === s.key;
        return (
          <button
            type="button"
            key={s.key}
            data-testid={`style-${s.key}`}
            onClick={() => onChange(s.key)}
            className={`rounded-xl border-2 bg-white/80 p-4 text-left transition ${
              on
                ? "border-orange-500 ring-2 ring-orange-300"
                : "border-orange-100 hover:border-orange-300"
            }`}
          >
            <div className="flex items-center gap-2">
              <Sparkles
                className={`h-4 w-4 ${
                  on ? "text-orange-600" : "text-stone-400"
                }`}
              />
              <div className="font-semibold text-stone-800">{s.label}</div>
            </div>
            <p className="mt-1 text-xs leading-relaxed text-stone-600">
              {s.description}
            </p>
          </button>
        );
      })}
    </div>
  );
}

function ViewGrid({ job, title = "Character sheet" }) {
  const views = job?.views || [];
  return (
    <div className="space-y-3">
      <div className="text-sm font-medium text-stone-700">{title}</div>
      <div
        data-testid="view-grid"
        className="grid grid-cols-2 gap-3 md:grid-cols-4"
      >
        {views.map((v) => {
          const ready = v.status === "done" && v.image_url;
          const failed = v.status === "failed";
          return (
            <div
              key={v.view}
              data-testid={`view-${v.view}`}
              className="overflow-hidden rounded-xl border border-orange-100 bg-white/70 shadow-sm"
            >
              <div className="aspect-square w-full bg-gradient-to-br from-amber-50 to-orange-100">
                {ready ? (
                  <img
                    src={absUrl(v.image_url) + `?t=${job.updated_at}`}
                    alt={v.view}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-stone-500">
                    {failed ? (
                      <span className="flex items-center gap-1 text-red-600">
                        <XCircle className="h-4 w-4" /> failed
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
                        {v.status}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="px-3 py-2 text-xs font-semibold text-stone-700">
                {VIEW_LABELS[v.view] || v.view}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Designer({ onCancel, onSaved, initialStyle, initialName, initialRefs }) {
  const [styles, setStyles] = useState([]);
  const [samples, setSamples] = useState([]);
  const [step, setStep] = useState(1);
  const [selectedRefs, setSelectedRefs] = useState(initialRefs || []);
  const [style, setStyle] = useState(initialStyle || "pixar_3d");
  const [name, setName] = useState(initialName || "Dom");
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [characterId, setCharacterId] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/characters/styles"),
      api.get("/characters/reference-samples"),
    ]).then(([s, r]) => {
      setStyles(s.data.styles || []);
      setSamples(r.data || []);
      if (!selectedRefs.length && r.data?.length) {
        setSelectedRefs(r.data.slice(0, 3).map((x) => x.url));
      }
    });
  }, []);

  const canGenerate = selectedRefs.length > 0 && !!style && !!name.trim();

  const start = async () => {
    setError(null);
    setStarting(true);
    try {
      const r = await api.post("/characters", {
        name: name.trim(),
        style,
        reference_photo_urls: selectedRefs,
      });
      setJob(r.data);
      setCharacterId(r.data.character_id);
      setStep(4);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    if (!job || !job.job_id) return;
    if (job.status === "completed" || job.status === "failed") return;
    const id = setInterval(async () => {
      try {
        const r = await api.get(`/characters/generation-jobs/${job.job_id}`);
        setJob(r.data);
        if (r.data.status === "completed" || r.data.status === "failed") {
          clearInterval(id);
        }
      } catch {
        /* ignore transient errors */
      }
    }, 2000);
    return () => clearInterval(id);
  }, [job?.job_id, job?.status]);

  const regenerate = async () => {
    if (!characterId) return;
    const r = await api.post(`/characters/${characterId}/regenerate`, {
      style,
      reference_photo_urls: selectedRefs,
    });
    setJob(r.data);
  };

  const save = async () => {
    if (!characterId) return;
    await api.patch(`/characters/${characterId}/active`, { is_active: true });
    onSaved?.(characterId);
  };

  return (
    <Card data-testid="designer-card" className="border-orange-200 bg-white/80 shadow-lg">
      <CardHeader>
        <CardTitle>Character designer</CardTitle>
        <CardDescription>
          Step {Math.min(step, 4)} of 4 · {[
            "Reference photos",
            "Style",
            "Name & generate",
            "Review",
          ][Math.min(step, 4) - 1]}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {step === 1 ? (
          <div className="space-y-3">
            <p className="text-sm text-stone-600">
              Pick <strong>2–3 reference photos</strong>. The character designer
              will use them as a visual anchor and produce a clearly stylized
              cartoon (never a photorealistic copy).
            </p>
            <ReferencePhotoPicker
              samples={samples}
              selected={selectedRefs}
              setSelected={setSelectedRefs}
              onUploadUrl={(u) =>
                setSelectedRefs((prev) =>
                  prev.includes(u) ? prev : [...prev, u],
                )
              }
            />
            <div className="text-xs text-stone-500">
              Selected: {selectedRefs.length}
            </div>
          </div>
        ) : null}

        {step === 2 ? (
          <StylePicker styles={styles} value={style} onChange={setStyle} />
        ) : null}

        {step === 3 ? (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-stone-700">
                Character name
              </label>
              <Input
                data-testid="character-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="max-w-sm border-orange-200"
              />
            </div>
            <div className="rounded-lg border border-orange-100 bg-amber-50 p-4 text-sm text-stone-700">
              <div>
                <strong>Style:</strong>{" "}
                {styles.find((s) => s.key === style)?.label || style}
              </div>
              <div>
                <strong>References:</strong> {selectedRefs.length}
              </div>
              <div className="mt-1 text-xs text-stone-500">
                We&apos;ll generate 4 views in parallel (portrait, side profile,
                full body, expression sheet). Typical run time: 10–40s.
              </div>
            </div>
          </div>
        ) : null}

        {step === 4 && job ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge
                data-testid="char-job-status"
                className={
                  job.status === "completed"
                    ? "bg-emerald-600 hover:bg-emerald-600"
                    : job.status === "failed"
                    ? "bg-red-600 hover:bg-red-600"
                    : "bg-amber-500 hover:bg-amber-500"
                }
              >
                {job.status}
              </Badge>
              <Badge className="bg-emerald-600 hover:bg-emerald-600">REAL</Badge>
              <span className="text-xs text-stone-600">
                nano-banana image generation
              </span>
            </div>
            <ViewGrid job={job} />
          </div>
        ) : null}

        {error ? (
          <div
            data-testid="designer-error"
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700"
          >
            {error}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-orange-100 pt-4">
          <div className="flex gap-2">
            {step === 4 ? (
              <>
                <Button
                  data-testid="save-character-btn"
                  onClick={save}
                  disabled={job?.status !== "completed"}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Star className="mr-2 h-4 w-4" />
                  This is {name}!
                </Button>
                <Button
                  data-testid="regenerate-btn"
                  onClick={regenerate}
                  variant="outline"
                  disabled={
                    job?.status !== "completed" && job?.status !== "failed"
                  }
                  className="border-orange-300"
                >
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Regenerate
                </Button>
                <Button
                  data-testid="try-different-style-btn"
                  onClick={() => setStep(2)}
                  variant="outline"
                  className="border-orange-300"
                  disabled={
                    job?.status !== "completed" && job?.status !== "failed"
                  }
                >
                  Try different style
                </Button>
              </>
            ) : (
              <Button variant="ghost" onClick={onCancel} data-testid="designer-cancel">
                Cancel
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {step > 1 && step < 4 ? (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                className="border-orange-300"
              >
                Back
              </Button>
            ) : null}
            {step < 3 ? (
              <Button
                data-testid="designer-next"
                onClick={() => setStep(step + 1)}
                disabled={
                  (step === 1 && selectedRefs.length === 0) ||
                  (step === 2 && !style)
                }
                className="bg-gradient-to-r from-orange-500 to-rose-500 text-white"
              >
                Next <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : step === 3 ? (
              <Button
                data-testid="designer-generate-btn"
                onClick={start}
                disabled={!canGenerate || starting}
                className="bg-gradient-to-r from-orange-500 to-rose-500 text-white"
              >
                {starting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="mr-2 h-4 w-4" />
                )}
                Generate character
              </Button>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Characters() {
  const [chars, setChars] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [designing, setDesigning] = useState(false);

  const refresh = async () => {
    const r = await api.get("/characters");
    setChars(r.data);
    setLoaded(true);
  };

  useEffect(() => {
    refresh();
  }, []);

  const activeChar = useMemo(() => chars.find((c) => c.is_active), [chars]);

  const setActive = async (id) => {
    await api.patch(`/characters/${id}/active`, { is_active: true });
    refresh();
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this character? This cannot be undone.")) return;
    await api.delete(`/characters/${id}`);
    refresh();
  };

  if (!loaded) {
    return (
      <div className="flex justify-center py-20 text-stone-500">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="page-characters" className="mx-auto max-w-6xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-stone-800">Characters</h1>
          <p className="text-sm text-stone-600">
            Your cast of child-safe, stylized cartoon characters for Dom&apos;s
            Journey.
          </p>
        </div>
        {chars.length > 0 && !designing ? (
          <Button
            data-testid="new-character-btn"
            onClick={() => setDesigning(true)}
            className="rounded-full bg-gradient-to-r from-orange-500 to-rose-500 text-white"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            New character
          </Button>
        ) : null}
      </div>

      {designing ? (
        <Designer
          onCancel={() => setDesigning(false)}
          onSaved={() => {
            setDesigning(false);
            refresh();
          }}
        />
      ) : chars.length === 0 ? (
        <EmptyState onStart={() => setDesigning(true)} />
      ) : (
        <div className="grid gap-5 md:grid-cols-2">
          {chars.map((c) => {
            const portrait = c.views?.find((v) => v.view === "portrait");
            return (
              <Card
                key={c.id}
                data-testid={`character-${c.id}`}
                className={`border bg-white/80 shadow-sm ${
                  c.is_active
                    ? "border-emerald-400 ring-2 ring-emerald-200"
                    : "border-orange-200"
                }`}
              >
                <CardHeader className="flex-row items-center gap-4 space-y-0">
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full bg-gradient-to-br from-amber-100 to-orange-200">
                    {portrait?.image_url ? (
                      <img
                        src={absUrl(portrait.image_url)}
                        alt={c.name}
                        className="h-full w-full object-cover"
                      />
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <CardTitle className="truncate text-lg">{c.name}</CardTitle>
                      {c.is_active ? (
                        <Badge className="bg-emerald-600 hover:bg-emerald-600">
                          Active
                        </Badge>
                      ) : null}
                    </div>
                    <CardDescription className="truncate">
                      {c.style_label} ·{" "}
                      {c.status === "ready"
                        ? "ready"
                        : c.status === "generating"
                        ? "generating…"
                        : "failed"}
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <ViewGrid
                    job={{ views: c.views, updated_at: c.updated_at }}
                    title=""
                  />
                  <div className="flex flex-wrap gap-2 pt-2">
                    {!c.is_active ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setActive(c.id)}
                        data-testid={`set-active-${c.id}`}
                        className="border-orange-300"
                      >
                        Set as active
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-orange-300"
                      data-testid={`regen-${c.id}`}
                      onClick={async () => {
                        await api.post(`/characters/${c.id}/regenerate`, {});
                        refresh();
                      }}
                    >
                      <RefreshCcw className="mr-1 h-3 w-3" />
                      Regenerate
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      data-testid={`delete-${c.id}`}
                      onClick={() => onDelete(c.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
